import type { ChatMessage } from "@ai-foundry/sdk";
import { getLoggerFor } from "@tylertech/logging";
import { USER_ACTIVITY_NEW } from "@tylertech/smart-pulse-shared";
import type { EventSubscriber, NewUserActivityEventPayload, UserActivity } from "@tylertech/smart-pulse-shared";

interface FollowMeSidebar extends HTMLElement
{
    sendMessage?(message: string): Promise<void>;
    getMessages?(): ChatMessage[];
}

const STORAGE_KEY = "sp-follow-me-enabled";
const SETTLE_MS = 1500;
const MIN_SEND_GAP_MS = 5000;
const MAX_PENDING_ACTIONS = 25;

export class FollowMeMode
{
    private readonly log = getLoggerFor("FollowMeMode");
    private disposed = false;
    private paused = false;
    private unsubscribeActivity?: () => void;
    private agentRunInProgress = false;
    private sendingToAgent = false;
    private lastSendAt = 0;
    private pendingActions: string[] = [];
    private unsettledActions: (() => string)[] = [];
    private debounceId: ReturnType<typeof setTimeout> | undefined;
    private retryId: ReturnType<typeof setTimeout> | undefined;

    constructor(
        private readonly sidebar: FollowMeSidebar,
        private readonly drawerOpen: () => boolean,
        private readonly events: EventSubscriber,
    ) {}

    get enabled(): boolean
    {
        return sessionStorage.getItem(STORAGE_KEY) === "true";
    }

    private get active(): boolean
    {
        return !this.disposed && !this.paused && this.enabled && this.drawerOpen();
    }

    init(): void
    {
        this.sidebar.addEventListener("foundry-chatbot-run-started", this.onRunStarted);
        this.sidebar.addEventListener("foundry-chatbot-run-finished", this.onRunDone);
        this.sidebar.addEventListener("foundry-chatbot-run-aborted", this.onRunDone);
        this.sidebar.addEventListener("foundry-sidebar-chatbot-open", this.onSendOpportunity);
        this.sidebar.addEventListener("foundry-chatbot-state-restored", this.onSendOpportunity);
        this.unsubscribeActivity = this.events.subscribe<NewUserActivityEventPayload>(
            USER_ACTIVITY_NEW,
            event => this.onActivity(event.payload.activity));
    }

    dispose(): void
    {
        this.disposed = true;
        this.sidebar.removeEventListener("foundry-chatbot-run-started", this.onRunStarted);
        this.sidebar.removeEventListener("foundry-chatbot-run-finished", this.onRunDone);
        this.sidebar.removeEventListener("foundry-chatbot-run-aborted", this.onRunDone);
        this.sidebar.removeEventListener("foundry-sidebar-chatbot-open", this.onSendOpportunity);
        this.sidebar.removeEventListener("foundry-chatbot-state-restored", this.onSendOpportunity);
        this.unsubscribeActivity?.();
        this.discardPending();
    }

    toggle(): void
    {
        sessionStorage.setItem(STORAGE_KEY, String(!this.enabled));
        if (!this.enabled)
            this.discardPending();
    }

    setPaused(paused: boolean): void
    {
        this.paused = paused;
        if (paused)
            this.discardPending();
    }

    noteNavigation(): void
    {
        this.record(() => `navigated to "${document.title || location.pathname}"`);
    }

    private readonly onRunStarted = (): void =>
    {
        this.agentRunInProgress = true;
    };

    private readonly onRunDone = (): void =>
    {
        this.agentRunInProgress = false;
        this.trySendPending();
    };

    private readonly onSendOpportunity = (): void =>
    {
        this.trySendPending();
    };

    private onActivity(activity: UserActivity): void
    {
        if (!activity.name)
            return;

        const action = this.describe(activity);
        this.record(() => action);
    }

    private describe(activity: UserActivity): string
    {
        switch (activity.type)
        {
            case "field":
                return activity.newValue
                    ? `filled in "${activity.name}" with "${this.formatValue(activity.newValue)}"`
                    : `cleared "${activity.name}"`;
            case "dropdown":
                return `opened "${activity.name}"`;
            case "checkbox":
                return activity.value === undefined
                    ? `toggled "${activity.name}"`
                    : `${activity.value ? "checked" : "unchecked"} "${activity.name}"`;
            default:
                return `clicked "${activity.name}"`;
        }
    }

    private formatValue(value: string): string
    {
        const date = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) ? new Date(value) : undefined;
        return date && !isNaN(date.getTime())
            ? date.toLocaleDateString()
            : value.slice(0, 100);
    }

    private record(action: () => string): void
    {
        if (!this.active)
            return;

        this.unsettledActions.push(action);
        clearTimeout(this.debounceId);
        this.debounceId = setTimeout(() => this.settle(), SETTLE_MS);
    }

    private settle(): void
    {
        this.pendingActions.push(...this.unsettledActions.map(action => action()));
        this.unsettledActions = [];
        if (this.pendingActions.length > MAX_PENDING_ACTIONS)
            this.pendingActions.splice(0, this.pendingActions.length - MAX_PENDING_ACTIONS);
        this.trySendPending();
    }

    private discardPending(): void
    {
        clearTimeout(this.debounceId);
        clearTimeout(this.retryId);
        this.retryId = undefined;
        this.pendingActions = [];
        this.unsettledActions = [];
    }

    private trySendPending(): void
    {
        if (!this.active || this.pendingActions.length === 0 || this.sendingToAgent || this.agentRunInProgress)
            return;
        if (!this.sidebar.sendMessage)
            return;

        const hasUserMessage = this.sidebar.getMessages?.().some(msg => msg.role === "user") ?? false;
        if (!hasUserMessage)
            return;

        const wait = MIN_SEND_GAP_MS - (Date.now() - this.lastSendAt);
        if (wait > 0)
        {
            this.retryId ??= setTimeout(() =>
            {
                this.retryId = undefined;
                this.trySendPending();
            }, wait);
            return;
        }

        const message = this.pendingActions.map(action => `(user-action) I ${action}.`).join("\n");
        this.pendingActions = [];
        this.sendingToAgent = true;
        this.lastSendAt = Date.now();

        this.sidebar.sendMessage(message)
            .catch(error => this.log.error("sendMessage failed", error))
            .finally(() =>
            {
                this.sendingToAgent = false;
                setTimeout(() => this.trySendPending(), 500);
            });
    }
}
